import { PageHeader } from '@/shared/components/PageHeader';
import { TabNav } from '@/shared/components/TabNav';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import {
  AlertCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  Loader2,
  Mail,
  Phone,
  Plus,
  Search,
  UserCircle,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { User } from '../types/user';
import { UserActionsMenu } from './UserActionsMenu';
import { UsersFiltersModal } from './UsersFiltersModal';
import { DeactivateUserDialog } from './dialogs/DeactivateUserDialog';
import { ReactivateUserDialog } from './dialogs/ReactivateUserDialog';
import { SetPasswordDialog } from './dialogs/SetPasswordDialog';

export interface AppliedFilters {
  name: string;
  email: string;
  jobTitle: string;
  team: string;
  activeJobsMin: string;
  activeJobsMax: string;
  completedJobsMin: string;
  completedJobsMax: string;
}
interface UsersViewProps {
  users: User[];
  isLoading?: boolean;
  isError?: boolean;
  totalCount: number;
  // Filter state (controlled from parent via URL)
  activeTab: string;
  search: string;
  nameFilter: string;
  emailFilter: string;
  jobTitleFilter: string;
  teamFilter: string;
  activeJobsMin: string;
  activeJobsMax: string;
  completedJobsMin: string;
  completedJobsMax: string;
  // Pagination
  page: number;
  totalPages: number;
  limit: number;
  // Callbacks
  onTabChange: (tab: string) => void;
  onSearchChange: (search: string) => void;
  onFiltersApply: (filters: AppliedFilters) => void;
  onFiltersClear: () => void;
  onPageChange: (page: number) => void;
  onUserClick?: (userId: string) => void;
  onAddUser?: () => void;
}

const statusConfig = {
  active: {
    label: 'Active',
    color:
      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border-green-300 dark:border-green-700',
  },
  away: {
    label: 'Away',
    color:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700',
  },
  offline: {
    label: 'Offline',
    color:
      'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100 border-slate-300 dark:border-slate-600',
  },
};

function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

export function UsersView({
  users,
  isLoading,
  isError,
  totalCount,
  activeTab,
  search,
  nameFilter,
  emailFilter,
  jobTitleFilter,
  teamFilter,
  activeJobsMin,
  activeJobsMax,
  completedJobsMin,
  completedJobsMax,
  page,
  totalPages,
  limit,
  onTabChange,
  onSearchChange,
  onFiltersApply,
  onFiltersClear,
  onPageChange,
  onUserClick,
  onAddUser,
}: UsersViewProps) {
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [searchInput, setSearchInput] = useState(search);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [passwordTarget, setPasswordTarget] = useState<User | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<User | null>(null);
  const [reactivateTarget, setReactivateTarget] = useState<User | null>(null);

  // Sync external search prop changes (e.g. from URL) into input
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const debouncedSearch = useCallback(
    (value: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => onSearchChange(value), 400);
    },
    [onSearchChange],
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  // Count active filters
  const activeFilters: { key: string; label: string; value: string }[] = [];
  if (nameFilter) activeFilters.push({ key: 'name', label: 'Name', value: nameFilter });
  if (emailFilter) activeFilters.push({ key: 'email', label: 'Email', value: emailFilter });
  if (jobTitleFilter !== 'all')
    activeFilters.push({ key: 'jobTitle', label: 'Role', value: jobTitleFilter });
  if (teamFilter !== 'all') activeFilters.push({ key: 'team', label: 'Team', value: teamFilter });
  if (activeJobsMin)
    activeFilters.push({ key: 'activeJobsMin', label: 'Active Jobs Min', value: activeJobsMin });
  if (activeJobsMax)
    activeFilters.push({ key: 'activeJobsMax', label: 'Active Jobs Max', value: activeJobsMax });
  if (completedJobsMin)
    activeFilters.push({
      key: 'completedJobsMin',
      label: 'Completed Min',
      value: completedJobsMin,
    });
  if (completedJobsMax)
    activeFilters.push({
      key: 'completedJobsMax',
      label: 'Completed Max',
      value: completedJobsMax,
    });

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onSearchChange('');
  };

  const handleRemoveFilter = (key: string) => {
    const current: AppliedFilters = {
      name: nameFilter,
      email: emailFilter,
      jobTitle: jobTitleFilter,
      team: teamFilter,
      activeJobsMin,
      activeJobsMax,
      completedJobsMin,
      completedJobsMax,
    };
    if (key === 'jobTitle' || key === 'team') {
      (current as any)[key] = 'all';
    } else {
      (current as any)[key] = '';
    }
    onFiltersApply(current);
  };

  const startIdx = (page - 1) * limit + 1;
  const endIdx = Math.min(page * limit, totalCount);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Users"
        subtitle={`${totalCount} users`}
        icon={<UserCircle className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
        actions={
          <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={onAddUser}>
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto px-1">
        {/* Tabs */}
        <div className="flex items-center justify-between mb-6">
          <TabNav
            tabs={[
              { id: 'normal', label: 'Normal Accounts' },
              { id: 'mobile', label: 'Mobile Users' },
              { id: 'inactive', label: 'Inactive' },
            ]}
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
        </div>

        {/* Search + Filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name, email, or team..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9 bg-card border-border text-foreground"
            />
            {searchInput && (
              <button
                onClick={handleSearchClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFiltersModal(true)}
            className="border-border hover:bg-accent"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {activeFilters.length > 0 && (
              <Badge variant="default" className="ml-2 bg-blue-600 text-white">
                {activeFilters.length}
              </Badge>
            )}
          </Button>
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-sm text-muted-foreground">Active filters:</span>
            {activeFilters.map((f) => (
              <Badge
                key={f.key}
                variant="outline"
                className="bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 pl-2 pr-1 gap-1"
              >
                {f.label}: {f.value}
                <button
                  onClick={() => handleRemoveFilter(f.key)}
                  className="ml-1 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-sm p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={onFiltersClear}
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader className="sticky top-0 bg-card/90 backdrop-blur-sm z-10">
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground font-medium">
                  <div className="flex items-center gap-1">
                    User
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </TableHead>
                <TableHead className="text-muted-foreground font-medium">Contact</TableHead>
                <TableHead className="text-muted-foreground font-medium">Role</TableHead>
                <TableHead className="text-muted-foreground font-medium">Team</TableHead>
                <TableHead className="text-muted-foreground font-medium">Status</TableHead>
                <TableHead className="text-muted-foreground font-medium">Jobs</TableHead>
                <TableHead className="text-muted-foreground font-medium text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className="border-border hover:bg-accent/50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-9 h-9 bg-orange-600 text-white">
                        <AvatarFallback className="bg-orange-600 text-white text-sm">
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-foreground">{user.name}</div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <a
                        href={`mailto:${user.email}`}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-orange-600 dark:hover:text-orange-400"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Mail className="w-3 h-3" />
                        <span>{user.email}</span>
                      </a>
                      <a
                        href={`tel:${user.phone.replace(/\s+/g, '')}`}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-orange-600 dark:hover:text-orange-400"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="w-3 h-3" />
                        <span>{user.phone}</span>
                      </a>
                    </div>
                  </TableCell>
                  <TableCell className="text-foreground font-medium">{user.role}</TableCell>
                  <TableCell className="text-foreground">{user.team}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`text-xs ${statusConfig[user.status]?.color || statusConfig.offline.color}`}
                    >
                      {statusConfig[user.status]?.label || 'Unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm text-foreground">{user.activeJobs} active</div>
                      <div className="text-xs text-muted-foreground">
                        {user.completedJobs} completed
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        onClick={() => onUserClick?.(user.id)}
                        title="View user details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <UserActionsMenu
                        isActive={user.status !== 'offline'}
                        onView={() => onUserClick?.(user.id)}
                        onEdit={() => onUserClick?.(user.id)}
                        onResetPassword={() => setPasswordTarget(user)}
                        onDeactivate={() => setDeactivateTarget(user)}
                        onReactivate={() => setReactivateTarget(user)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* States */}
          {isLoading && (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground">
              <div className="text-center">
                <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
                <p>Loading users...</p>
              </div>
            </div>
          )}
          {isError && !isLoading && (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400 opacity-70" />
                <p>Failed to load users</p>
              </div>
            </div>
          )}
          {!isLoading && !isError && users.length === 0 && (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground">
              <div className="text-center">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No users found</p>
              </div>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Showing {startIdx}–{endIdx} of {totalCount} users
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
                className="h-8"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium text-foreground">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
                className="h-8"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Filters Modal */}
      <UsersFiltersModal
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        nameFilter={nameFilter}
        emailFilter={emailFilter}
        jobTitleFilter={jobTitleFilter}
        teamFilter={teamFilter}
        activeJobsMin={activeJobsMin}
        activeJobsMax={activeJobsMax}
        completedJobsMin={completedJobsMin}
        completedJobsMax={completedJobsMax}
        onApply={(applied) => {
          onFiltersApply(applied);
          setShowFiltersModal(false);
        }}
        onClear={() => {
          onFiltersClear();
          setShowFiltersModal(false);
        }}
      />

      {passwordTarget && (
        <SetPasswordDialog
          open={!!passwordTarget}
          onOpenChange={(open) => !open && setPasswordTarget(null)}
          userId={passwordTarget.id}
          userName={passwordTarget.name}
        />
      )}
      {deactivateTarget && (
        <DeactivateUserDialog
          open={!!deactivateTarget}
          onOpenChange={(open) => !open && setDeactivateTarget(null)}
          userId={deactivateTarget.id}
          userName={deactivateTarget.name}
        />
      )}
      {reactivateTarget && (
        <ReactivateUserDialog
          open={!!reactivateTarget}
          onOpenChange={(open) => !open && setReactivateTarget(null)}
          userId={reactivateTarget.id}
          userName={reactivateTarget.name}
        />
      )}
    </div>
  );
}
